import { MatDialog } from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { ApiService } from 'app/shared/apis/api.service';
import { BaseService } from 'app/core/services/base.service';
import { CnpjData, CompanyModel } from 'app/models/company.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { StorageKeysService } from 'app/shared/services/storage-keys.service';
import { ThemePaletteService } from './theme-palette.service';
import { SERVICE_SHARE_TOKEN_KEY } from './service-share.service';
import {
    isOperationalCompanyKind,
    isRequesterCompanyKind,
    usesPrehoxApp,
} from 'app/core/auth/requester-user';
import { AccessApprovalService } from './access-approval.service';

@Injectable({
    providedIn: 'root',
})
export class CompanyService extends BaseService<CompanyModel> {
    private _currentCompany = new BehaviorSubject<CompanyModel>(null);
    currentCompany$: Observable<CompanyModel> = this._currentCompany.asObservable();

    /** Snapshot da empresa operacional ao entrar no portal solicitante. */
    private readonly operationalRestoreKey = 'operational_company_restore_id';
    /** Chave legada do preview admin — ainda lida no restore. */
    private readonly legacyRequesterPreviewKey = 'requester_preview_restore_company_id';

    constructor(
        api: ApiService,
        _matDialog: MatDialog,
        private _skey: StorageKeysService,
        private themePaletteService: ThemePaletteService,
        private accessApprovalService: AccessApprovalService,
    ) {
        super(api, _matDialog, 'company');

        const savedCompany = localStorage.getItem(this._skey.companyId);
        if (savedCompany) {
            const company = new CompanyModel(JSON.parse(savedCompany));
            // Parceira externa (sem app) não pode ficar como tenant operacional.
            if (isOperationalCompanyKind(company.company_kind) && !usesPrehoxApp(company)) {
                if (localStorage.getItem(this._skey.token) && !this.isPublicShareContext()) {
                    this.autoSelectFirstCompany(true).subscribe();
                }
            } else {
                this._currentCompany.next(company);
                this.notifyCompanyAccess(company);
            }
        } else if (localStorage.getItem(this._skey.token) && !this.isPublicShareContext()) {
            // Sem token (tela de login) essa listagem 401 e o interceptor recarregava a página em loop.
            this.autoSelectFirstCompany().subscribe();
        }
    }

    /**
     * Seleciona automaticamente a primeira empresa operacional (manager/client)
     * que usa o app Prehox, quando ainda não há empresa corrente.
     * Usado tanto no boot do app (token já salvo no localStorage) quanto logo
     * após um login bem-sucedido: como este serviço é singleton `providedIn:
     * 'root'`, seu construtor não roda de novo na navegação client-side
     * pós-login, então sem essa chamada explícita nenhuma empresa é
     * selecionada para usuários sem vínculo com empresa solicitante.
     */
    autoSelectFirstCompany(force = false): Observable<CompanyModel | null> {
        if (!force && this._currentCompany.value) {
            return of(this._currentCompany.value);
        }
        return this.listAppTenantCompanies().pipe(
            map((items) => {
                if (!force && this._currentCompany.value) {
                    return this._currentCompany.value;
                }
                const first = items?.[0];
                if (!first) {
                    return null;
                }
                const company = new CompanyModel(first);
                this.setCurrentCompany(company);
                return company;
            }),
            catchError(() => of(null)),
        );
    }

    /** Empresas manager/client que usam o app (contexto operacional / sidenav). */
    listAppTenantCompanies(): Observable<CompanyModel[]> {
        return this.list({ limit: 'all', company_kind: 'manager' } as any).pipe(
            map((response) => (response?.items || []).filter((c) => usesPrehoxApp(c))),
            catchError(() => of([])),
        );
    }

    /** Parceiras de despacho externo (sem app Prehox). */
    listExternalDispatchPartners(): Observable<CompanyModel[]> {
        return this.list({ limit: 'all', company_kind: 'manager' } as any).pipe(
            map((response) =>
                (response?.items || [])
                    .map((c) => new CompanyModel(c))
                    .filter((c) => !usesPrehoxApp(c)),
            ),
            catchError(() => of([])),
        );
    }

    private isPublicShareContext(): boolean {
        const onSharePage =
            typeof location !== 'undefined' && location.pathname.includes('/acompanhar/');
        const hasShareToken =
            typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem(SERVICE_SHARE_TOKEN_KEY);
        return onSharePage || hasShareToken;
    }

    setCurrentCompany(company: CompanyModel): void {
        const mappedCompany = new CompanyModel(company);
        // Bloqueia persistir parceira externa como tenant operacional do app.
        if (isOperationalCompanyKind(mappedCompany.company_kind) && !usesPrehoxApp(mappedCompany)) {
            if (this._currentCompany.value?.company_id === mappedCompany.company_id) {
                this.autoSelectFirstCompany(true).subscribe();
            }
            return;
        }
        this._currentCompany.next(mappedCompany);
        localStorage.setItem(this._skey.companyId, JSON.stringify(mappedCompany));
        this.themePaletteService.applyPrimaryColor(mappedCompany.primary_color || '#0275bc');
        this.notifyCompanyAccess(mappedCompany);
    }

    /**
     * Registra "usuário está usando esta empresa" (KPI "Acessos por Empresa")
     * e verifica a aprovação de entrada por médico remoto. Disparado sempre
     * que a empresa atual é resolvida — no boot do app (reidratação do
     * localStorage), na auto-seleção (usuário com 1 empresa só) e na troca
     * manual no seletor — não só no login. Isso cobre sessões longas (refresh
     * token de 30 dias) sem depender de um novo login.
     *
     * Não silencioso para o resultado da aprovação: atualiza
     * `AccessApprovalService.state$`, que o `AccessApprovalGuard` usa para
     * bloquear a navegação enquanto o pedido não for aprovado. Falha de rede
     * (ex.: ainda não autenticado) continua não bloqueando nada.
     */
    private notifyCompanyAccess(company: CompanyModel): void {
        if (!company?.company_id || !ApiService.user.value) {
            return;
        }
        this.accessApprovalService.checkCompanyAccess(company.company_id).subscribe({ error: () => {} });
    }

    /**
     * Define empresa em memória sem persistir no localStorage (viewer público).
     */
    setCurrentCompanyEphemeral(company: Partial<CompanyModel> | CompanyModel): void {
        const mappedCompany = new CompanyModel(company);
        this._currentCompany.next(mappedCompany);
        if (mappedCompany.primary_color) {
            this.themePaletteService.applyPrimaryColor(mappedCompany.primary_color);
        }
    }

    getCurrentCompany(): CompanyModel {
        return this._currentCompany.value;
    }

    uploadLogo(formData: FormData): Observable<any> {
        return this.api.postSub(`${this.endpoint}/uploadLogo`, formData);
    }

    uploadAfe(formData: FormData): Observable<{ url: string }> {
        return this.api.postSub<{ url: string }>(`${this.endpoint}/uploadAfe`, formData);
    }

    getByCnpj(cnpj: string): Observable<CnpjData> {
        return this.api.getSub<CnpjData>(`${this.endpoint}/by-cnpj/${cnpj}`);
    }

    getByVat(countryCode: string, vatNumber: string): Observable<any> {
        const cc = String(countryCode || '')
            .trim()
            .toUpperCase();
        const encoded = encodeURIComponent(String(vatNumber || '').trim());
        return this.api.getSub(`${this.endpoint}/by-vat/${cc}/${encoded}`);
    }

    listMyRequesterCompanies(): Observable<CompanyModel[]> {
        return this.api.getSub<CompanyModel[]>(`${this.endpoint}/requesters/mine`);
    }

    resolveFirstRequesterCompany(): Observable<CompanyModel | null> {
        return this.listMyRequesterCompanies().pipe(
            map((items) => {
                const first = items?.[0];
                return first ? new CompanyModel(first) : null;
            }),
            catchError(() => of(null)),
        );
    }

    listAllRequesterCompanies(): Observable<CompanyModel[]> {
        return this.list({ limit: 'all', company_kind: 'requester' }).pipe(
            map((response) => response?.items || []),
            catchError(() => of([])),
        );
    }

    resolveFirstRequesterCompanyForPortal(isAdmin: boolean): Observable<CompanyModel | null> {
        const source$ = isAdmin ? this.listAllRequesterCompanies() : this.listMyRequesterCompanies();
        return source$.pipe(
            map((items) => {
                const list = items || [];
                const current = this.getCurrentCompany();
                if (current?.company_id && isRequesterCompanyKind(current.company_kind)) {
                    const match = list.find(
                        (item) => Number(item.company_id) === Number(current.company_id),
                    );
                    if (match) {
                        return new CompanyModel(match);
                    }
                }
                const first = list[0];
                return first ? new CompanyModel(first) : null;
            }),
            catchError(() => of(null)),
        );
    }

    /**
     * Guarda a empresa operacional atual antes de trocar para solicitante,
     * para restaurar ao sair do portal (sidebar/seletor).
     */
    rememberOperationalCompanyIfNeeded(): void {
        const current = this.getCurrentCompany();
        if (!current?.company_id || !isOperationalCompanyKind(current.company_kind) || !usesPrehoxApp(current)) {
            return;
        }
        try {
            sessionStorage.setItem(this.operationalRestoreKey, String(current.company_id));
        } catch {
            /* sessionStorage indisponível */
        }
    }

    /**
     * Se a empresa atual for solicitante e o usuário saiu do portal,
     * restaura a operacional salva (ou a primeira manager disponível que usa o app).
     */
    restoreOperationalCompany(): Observable<CompanyModel | null> {
        const current = this.getCurrentCompany();
        if (!isRequesterCompanyKind(current?.company_kind)) {
            return of(current);
        }

        let savedId: string | null = null;
        try {
            savedId =
                sessionStorage.getItem(this.operationalRestoreKey) ||
                sessionStorage.getItem(this.legacyRequesterPreviewKey);
        } catch {
            savedId = null;
        }

        return this.listAppTenantCompanies().pipe(
            map((items) => {
                const restored = savedId
                    ? items.find((item) => String(item.company_id) === savedId)
                    : null;
                const next = restored || items[0] || null;
                if (!next) {
                    return null;
                }
                this.setCurrentCompany(next);
                try {
                    sessionStorage.removeItem(this.operationalRestoreKey);
                    sessionStorage.removeItem(this.legacyRequesterPreviewKey);
                } catch {
                    /* ignore */
                }
                return new CompanyModel(next);
            }),
            catchError(() => of(null)),
        );
    }
}
